// Ported from the native app's StockDirectory.swift.

import { Emitter } from "./storage.js";

export type AssetClass = "Stocks" | "Futures" | "Forex" | "Crypto" | "Indices" | "Commodities";
export const assetClasses: AssetClass[] = ["Stocks", "Futures", "Forex", "Crypto", "Indices", "Commodities"];

export interface KnownStock {
  ticker: string;
  name: string;
  category: AssetClass;
}

// Futures, forex, crypto, and indices don't have a free official registry
// the way SEC-listed equities do, so these are hand-curated — major/liquid
// symbols a trader would recognize, using the ticker syntax Yahoo Finance's
// chart endpoint expects for each asset class.
export const curated: KnownStock[] = [
  { ticker: "GC=F", name: "Gold Futures", category: "Futures" },
  { ticker: "SI=F", name: "Silver Futures", category: "Futures" },
  { ticker: "CL=F", name: "Crude Oil Futures", category: "Futures" },
  { ticker: "NG=F", name: "Natural Gas Futures", category: "Futures" },
  { ticker: "ZC=F", name: "Corn Futures", category: "Futures" },
  { ticker: "ZS=F", name: "Soybean Futures", category: "Futures" },
  { ticker: "ES=F", name: "E-mini S&P 500 Futures", category: "Futures" },
  { ticker: "NQ=F", name: "E-mini Nasdaq 100 Futures", category: "Futures" },
  { ticker: "YM=F", name: "E-mini Dow Futures", category: "Futures" },
  { ticker: "RTY=F", name: "E-mini Russell 2000 Futures", category: "Futures" },
  { ticker: "ZB=F", name: "30-Year T-Bond Futures", category: "Futures" },
  { ticker: "HG=F", name: "Copper Futures", category: "Futures" },
  { ticker: "PL=F", name: "Platinum Futures", category: "Futures" },
  { ticker: "XAUUSD", name: "Gold Spot / US Dollar", category: "Commodities" },
  { ticker: "EURUSD=X", name: "Euro / US Dollar", category: "Forex" },
  { ticker: "GBPUSD=X", name: "British Pound / US Dollar", category: "Forex" },
  { ticker: "USDJPY=X", name: "US Dollar / Japanese Yen", category: "Forex" },
  { ticker: "USDCHF=X", name: "US Dollar / Swiss Franc", category: "Forex" },
  { ticker: "AUDUSD=X", name: "Australian Dollar / US Dollar", category: "Forex" },
  { ticker: "USDCAD=X", name: "US Dollar / Canadian Dollar", category: "Forex" },
  { ticker: "NZDUSD=X", name: "New Zealand Dollar / US Dollar", category: "Forex" },
  { ticker: "EURGBP=X", name: "Euro / British Pound", category: "Forex" },
  { ticker: "EURJPY=X", name: "Euro / Japanese Yen", category: "Forex" },
  { ticker: "GBPJPY=X", name: "British Pound / Japanese Yen", category: "Forex" },
  { ticker: "BTC-USD", name: "Bitcoin", category: "Crypto" },
  { ticker: "ETH-USD", name: "Ethereum", category: "Crypto" },
  { ticker: "SOL-USD", name: "Solana", category: "Crypto" },
  { ticker: "XRP-USD", name: "XRP", category: "Crypto" },
  { ticker: "DOGE-USD", name: "Dogecoin", category: "Crypto" },
  { ticker: "ADA-USD", name: "Cardano", category: "Crypto" },
  { ticker: "BNB-USD", name: "BNB", category: "Crypto" },
  { ticker: "LTC-USD", name: "Litecoin", category: "Crypto" },
  { ticker: "AVAX-USD", name: "Avalanche", category: "Crypto" },
  { ticker: "DOT-USD", name: "Polkadot", category: "Crypto" },
  { ticker: "^GSPC", name: "S&P 500", category: "Indices" },
  { ticker: "^DJI", name: "Dow Jones Industrial Average", category: "Indices" },
  { ticker: "^IXIC", name: "Nasdaq Composite", category: "Indices" },
  { ticker: "^RUT", name: "Russell 2000", category: "Indices" },
  { ticker: "^VIX", name: "CBOE Volatility Index", category: "Indices" },
  { ticker: "^FTSE", name: "FTSE 100", category: "Indices" },
  { ticker: "^N225", name: "Nikkei 225", category: "Indices" },
  { ticker: "^GDAXI", name: "DAX", category: "Indices" },
  { ticker: "^HSI", name: "Hang Seng Index", category: "Indices" },
];

interface SECTickerEntry {
  cik_str: number;
  ticker: string;
  title: string;
}

function capitalized(title: string): string {
  return title
    .toLowerCase()
    .replace(/(^|\s|[-/(])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase());
}

/**
 * A single shared fetch of SEC EDGAR's official company ticker list
 * (~10,000+ SEC-registered companies), merged with the curated lists above.
 * Powers the watchlist's autocomplete/name resolution and category filter,
 * and the CIK lookups insider trading data needs.
 *
 * Note: browsers forbid setting a custom User-Agent on fetch (the native app
 * sent one per SEC's guidance) — the browser's own UA is sent instead, which
 * still identifies the request as a real browser client.
 */
class StockDirectory {
  stocks: KnownStock[] = [];
  readonly changed = new Emitter();
  private cikByTicker: Record<string, string> = {};
  private categoryByTicker: Record<string, AssetClass> = {};
  private nameByTicker: Record<string, string> = {};
  private loadPromise: Promise<void> | null = null;

  ensureLoaded(): Promise<void> {
    this.loadPromise ??= this.load();
    return this.loadPromise;
  }

  private async load(): Promise<void> {
    const list: KnownStock[] = [...curated];
    const cikMap: Record<string, string> = {};
    try {
      const response = await fetch("https://www.sec.gov/files/company_tickers.json");
      if (response.ok) {
        const decoded = (await response.json()) as Record<string, SECTickerEntry>;
        for (const entry of Object.values(decoded)) {
          const ticker = entry.ticker.toUpperCase();
          list.push({ ticker, name: capitalized(entry.title), category: "Stocks" });
          cikMap[ticker] = String(entry.cik_str).padStart(10, "0");
        }
      }
    } catch {
      // Curated lists still work without the SEC registry.
    }
    list.sort((a, b) => (a.ticker < b.ticker ? -1 : 1));
    this.stocks = list;
    this.cikByTicker = cikMap;
    this.categoryByTicker = {};
    this.nameByTicker = {};
    for (const stock of list) { this.categoryByTicker[stock.ticker] = stock.category; this.nameByTicker[stock.ticker] = stock.name; }
    this.changed.emit();
  }

  async cik(ticker: string): Promise<string | undefined> {
    await this.ensureLoaded();
    return this.cikByTicker[ticker.toUpperCase()];
  }

  category(ticker: string): AssetClass | undefined {
    return this.categoryByTicker[ticker.toUpperCase()];
  }

  /** The full company/instrument name for a ticker, if known (curated list +
   * the SEC registry). Returns undefined until the directory has loaded, or for
   * an unrecognised symbol — callers should fall back to the ticker. Sync so it
   * can be read during render; the page re-renders on `changed` once loaded. */
  name(ticker: string): string | undefined {
    return this.nameByTicker[ticker.toUpperCase()];
  }

  resolveTicker(input: string): string | null {
    const trimmed = input.trim();
    if (!trimmed) return null;
    const upper = trimmed.toUpperCase();
    if (this.stocks.some((s) => s.ticker === upper)) return upper;
    const lower = trimmed.toLowerCase();
    const byName = this.stocks.find((s) => s.name.toLowerCase() === lower);
    if (byName) return byName.ticker;
    const byPartial = this.stocks.find((s) => s.name.toLowerCase().includes(lower));
    if (byPartial) return byPartial.ticker;
    return null;
  }

  suggestions(query: string, category: AssetClass | null = null, limit = 8): KnownStock[] {
    const pool = category == null ? this.stocks : this.stocks.filter((s) => s.category === category);
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return pool.slice(0, limit);
    return pool
      .filter((s) => s.ticker.toLowerCase().startsWith(trimmed) || s.name.toLowerCase().includes(trimmed))
      .slice(0, limit);
  }
}

export const stockDirectory = new StockDirectory();
